import { effect, inject, Injectable, signal } from '@angular/core';

import { AuthStore, type SessionActor } from '../auth';
import { StorageService } from '../storage';
import { DEFAULT_ACCENT, onAccentColor, parseAccentHex } from './accent-color';

const ACCENT_STORAGE_KEY_PREFIX = 'ultrastock.accent-color.';

/** Storage key for a user's accent: per user, not per store. */
const accentStorageKeyFor = (userId: string): string => `${ACCENT_STORAGE_KEY_PREFIX}${userId}`;

/**
 * Root store for the user-selected accent color (openspec `user-accent-color`, #68).
 * Keeps the in-memory accent, applies `--accent` / `--on-accent` on
 * `document.documentElement`, and persists it per user via `StorageService`.
 */
@Injectable({ providedIn: 'root' })
export class AccentThemeStore {
  private readonly auth = inject(AuthStore);
  private readonly storage = inject(StorageService);

  private readonly accentSignal = signal<string>(DEFAULT_ACCENT);

  /** Current accent as a lowercase `#rrggbb` string. */
  readonly accent = this.accentSignal.asReadonly();

  constructor() {
    effect(() => {
      this.loadAccentForActor(this.auth.actor());
    });
  }

  /**
   * Validates and applies a new accent in memory + CSS, then persists it for
   * the signed-in user. Invalid hex is ignored. A failed write (e.g.
   * QuotaExceededError) keeps the in-session color.
   */
  setAccent(hex: string): void {
    const normalized = parseAccentHex(hex);

    if (normalized === null) {
      return;
    }

    this.applyAccent(normalized);

    const actor = this.auth.actor();

    if (actor === null) {
      return;
    }

    try {
      this.storage.set(accentStorageKeyFor(actor.userId), normalized);
    } catch (e) {
      console.warn(`[AccentThemeStore] Failed to persist accent for user: ${actor.userId}`, e);
    }
  }

  /** Unsigned users always see the brand default; signed-in users see their stored color. */
  private loadAccentForActor(actor: SessionActor | null): void {
    if (actor === null) {
      this.applyAccent(DEFAULT_ACCENT);
      return;
    }

    const stored = this.storage.get<string>(accentStorageKeyFor(actor.userId));
    this.applyAccent(parseAccentHex(stored) ?? DEFAULT_ACCENT);
  }

  private applyAccent(hex: string): void {
    this.accentSignal.set(hex);
    document.documentElement.style.setProperty('--accent', hex);
    document.documentElement.style.setProperty('--on-accent', onAccentColor(hex));
  }
}
